import React, { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { AdminUser } from '../../../lib/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../ui/sheet';
import { PrimaryButton, ToolButton } from '../dash/Toolbar';
import { Field, Select, TextInput } from '../editor/fields';

export interface UserFormValues {
  email: string;
  name: string;
  role: 'admin' | 'staff';
  password: string;
}

const emptyForm: UserFormValues = { email: '', name: '', role: 'staff', password: '' };

/** Side panel for creating or editing an admin user. */
export const UserPanel: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingUser: AdminUser | null;
  isSelf: boolean;
  saving: boolean;
  error: string | null;
  onSubmit: (values: UserFormValues) => void;
}> = ({ open, onOpenChange, editingUser, isSelf, saving, error, onSubmit }) => {
  const [values, setValues] = useState<UserFormValues>(emptyForm);

  useEffect(() => {
    if (!open) return;
    setValues(
      editingUser
        ? { email: editingUser.email, name: editingUser.name || '', role: editingUser.role, password: '' }
        : emptyForm
    );
  }, [open, editingUser]);

  const set = <K extends keyof UserFormValues>(key: K, value: UserFormValues[K]) => setValues((v) => ({ ...v, [key]: value }));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] p-0 font-poppins sm:max-w-sm">
        <SheetHeader className="border-b border-[color:var(--oa-line-2)] p-4">
          <SheetTitle className="font-montserrat text-[16px] font-extrabold text-[color:var(--oa-text)]">
            {editingUser ? 'Edit user' : 'Add user'}
          </SheetTitle>
        </SheetHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-auto p-4">
          {error ? (
            <p className="rounded-md bg-[color:color-mix(in_oklab,var(--oa-bad)_12%,transparent)] px-3 py-2 text-[12.5px] text-[color:var(--oa-bad)]">{error}</p>
          ) : null}

          <Field label="Email address" hint={editingUser ? 'Email cannot be changed' : undefined}>
            {/* autoComplete stops Chrome filling the signed-in admin's saved login into a new account. */}
            <TextInput
              type="email"
              name="new-user-email"
              autoComplete="off"
              value={values.email}
              disabled={!!editingUser}
              onChange={(e) => set('email', e.target.value)}
              placeholder="user@example.com"
            />
          </Field>

          {!editingUser ? (
            <Field label="Password" hint="Minimum 6 characters">
              <TextInput type="password" name="new-user-password" autoComplete="new-password" value={values.password} onChange={(e) => set('password', e.target.value)} placeholder="••••••••" />
            </Field>
          ) : null}

          <Field label="Name">
            <TextInput value={values.name} onChange={(e) => set('name', e.target.value)} placeholder="Full name" />
          </Field>

          <Field label="Role" hint={isSelf ? 'You cannot change your own role' : undefined}>
            <Select value={values.role} disabled={isSelf} onChange={(e) => set('role', e.target.value as 'admin' | 'staff')}>
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
            </Select>
          </Field>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[color:var(--oa-line-2)] p-4">
          <ToolButton onClick={() => onOpenChange(false)}>Cancel</ToolButton>
          <PrimaryButton onClick={() => onSubmit(values)} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {editingUser ? 'Save changes' : 'Create user'}
          </PrimaryButton>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default UserPanel;
